import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { BULK_WRITES_REDUCER_KEY, type BulkWrite, type BulkWritesStore } from './bulkWritesTypes';

const initialState: BulkWritesStore = {
  operations: {},
};

const slice = createSlice({
  name: BULK_WRITES_REDUCER_KEY,
  initialState,
  reducers: {
    bulkWriteStarted(state, { payload }: PayloadAction<BulkWrite>) {
      // A failed entry lingers until it is dismissed, so a new write supersedes any still sitting
      // around — otherwise one that is never dismissed accumulates.
      for (const [id, operation] of Object.entries(state.operations)) {
        if (operation.failure) {
          delete state.operations[id];
        }
      }
      state.operations[payload.id] = payload;
    },
    // Keyed by id so overlapping bulk writes are tracked independently, and so progress arriving
    // after the operation finished is dropped rather than resurrecting it.
    bulkWriteProgressed(state, { payload }: PayloadAction<{ id: string; submitted: number }>) {
      const operation = state.operations[payload.id];
      if (operation) {
        operation.submitted = payload.submitted;
      }
    },
    // Keeps the entry, unlike bulkWriteFinished, so the failure can be reported before it is
    // dismissed. Dismissing it dispatches bulkWriteFinished.
    bulkWriteFailed(state, { payload }: PayloadAction<{ id: string; committed: number; retryQueued: boolean }>) {
      const operation = state.operations[payload.id];
      if (operation) {
        operation.failure = { committed: payload.committed, retryQueued: payload.retryQueued };
      }
    },
    bulkWriteFinished(state, { payload }: PayloadAction<string>) {
      delete state.operations[payload];
    },
  },
});

export default slice.reducer;
export const bulkWritesActions = slice.actions;
